import {Component, OnInit, ViewChild} from '@angular/core';
import {Shipment} from '../../../domain/shipment';
import {Carrier} from '../../../domain/carrier';
import {ActivatedRoute, NavigationEnd, Params, Router} from '@angular/router';

import shortUuid from 'short-uuid';
import {filter} from 'rxjs/operators';
import {Sort} from '@angular/material/sort';
import {ShipmentService} from '../shipment.service';
import {Session} from '../../../domain/session';
import {PopupService} from '../../../component/popup.service';
import {ConfirmationDialogComponent} from '../../../component/confirmation-dialog/confirmation-dialog.component';
import {ShipmentListComponent} from '../shipment-list/shipment-list.component';
import {MatDialog} from '@angular/material/dialog';
import {PageEvent} from '@angular/material/paginator';
import {AssignOrderCarrierComponent} from '../assign-carrier-dialog/assign-order-carrier.component';
import {MessageService} from '../../../component/message.service';
import {RequestExecutor} from '../../../util/request-executor';

@Component({
  selector: 'app-assign-shipments',
  templateUrl: './assign-shipments.component.html',
  styleUrls: ['./assign-shipments.component.css'],
  standalone: false
})
export class AssignShipmentsComponent implements OnInit {
  totalShipmentCount: number;
  shipments: Shipment[];
  carriers: Carrier[];
  searchQuery: string;

  multiple: boolean = false;
  currentPage = 0;
  currentCarrier: Carrier = null;
  currentStatus: string;
  currentSortBy: string;
  currentSortDirection: string;
  currentPageSize = 20;

  selectedShipments: Shipment[] = [];

  progress = false;

  @ViewChild(ShipmentListComponent) list;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private request: RequestExecutor,
    session: Session,
    private navigateService: PopupService,
    private dialog: MatDialog,
    private messageService: MessageService
  ) {
    this.carriers = session.getCarriers().filter(value => value.Active);
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params: Params) => {
      if (params['page'] !== undefined) {
        this.currentPage = +params['page'] - 1;
      } else {
        this.currentPage = 0;
      }

      this.currentCarrier = this.getCarrierById(params['carrier']);
      this.currentStatus = params['status'];
      this.currentPageSize = params['pageSize'] == null ? 20 : params['pageSize'];
      this.searchQuery = params['search'];
    });

    this.initValue();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.initValue();
      });
  }

  private getCarrierById(id: string) {
    if (id === undefined) {
      return null;
    }

    let carrier = this.carriers.find(r => r.Id.toLowerCase() === shortUuid().toUUID(id));

    if (carrier === undefined) {
      return null;
    }
    return carrier;
  }

  private initValue() {
    this.totalShipmentCount = this.route.snapshot.data['shipments'].totalShipmentCount;
    this.shipments = this.route.snapshot.data['shipments'].shipments;

    this.selectedShipments = [];
    this.multiple = false;
  }

  changeFilteredCarrier(carrier: Carrier) {
    this.currentPage = 0;
    this.currentCarrier = carrier;
    this.navigateWithNewParameters();
  }

  changeFilteredStatus(status: string) {
    this.currentPage = 0;
    this.currentStatus = status;
    this.navigateWithNewParameters();
  }

  changePage(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.currentPageSize = event.pageSize;

    this.navigateWithNewParameters();
  }

  navigateWithNewParameters() {
    const params = {
      page: this.currentPage + 1,
      pageSize: this.currentPageSize,
      status: this.currentStatus,
      sortBy: this.currentSortBy,
      sortDirection: this.currentSortDirection,
      search: this.searchQuery
    };

    if (this.currentCarrier !== null) {
      params['carrier'] = shortUuid().fromUUID(this.currentCarrier.Id);
    }

    this.clearSelection();
    this.multiple = false;

    this.router.navigate(['/admin/shipments/assign'], {queryParams: params});
  }

  sortChanged(sort: Sort) {
    this.currentSortBy = sort.active;
    this.currentSortDirection = sort.direction === 'desc' ? 'Desc' : 'Asc';

    this.navigateWithNewParameters();
  }

  search() {
    this.navigateWithNewParameters();
  }

  assignShipment(shipments: Shipment[]) {
    this.navigateService.openFromComponent(AssignOrderCarrierComponent, {orderIds: shipments.map(s => s.OrderId)})
      .subscribe(() => {
        this.navigateWithNewParameters();
      });
  }

  queryUnassignShipment(shipments: Shipment[]) {
    let dialogRef = this.dialog.open(ConfirmationDialogComponent);

    dialogRef.componentInstance.title = 'Bekreft endring';
    dialogRef.componentInstance.description = 'Vil du fjerne tildeling av sendingen' + (shipments.length > 1 ? 'e' : '') + '?';

    dialogRef.afterClosed()
      .subscribe(value => {
        if (value) {
          this.unassignShipments(shipments.map(s => s.OrderId));
        }
      });
  }

  unassignShipments(orderIds: string[]) {
    this.progress = true;

    this.request.post('/order/unassign/batch', {OrderIds: orderIds})
      .then(() => {
        this.messageService.showBottomSuccessSnackBar('Tildeling ble fjernet for sendingen' + (orderIds.length > 1 ? 'e' : ''));

        this.navigateWithNewParameters();
      })
      .catch(error => {
        if (error.status == 401) {
          this.router.navigate(['login']);
        } else {
          this.messageService.showBottomErrorSnackBar('Kunne ikke fjerne tildeling av sendingen' + (orderIds.length > 1 ? 'e' : ''));
        }
      })
      .finally(() => this.progress = false);
  }

  toggleMultipleSelect() {
    this.multiple = !this.multiple;

    this.selectedShipments = [];
  }

  selectionToggled(shipment: Shipment) {
    let existing = this.selectedShipments.findIndex(s => s.Number === shipment.Number);

    if (existing === -1) {
      this.selectedShipments.push(shipment);
    } else {
      this.selectedShipments.splice(existing, 1);
    }
  }

  assignSelectedShipments() {
    this.assignShipment(this.selectedShipments);
  }

  unassignSelectedShipments() {
    this.queryUnassignShipment(this.selectedShipments);
  }

  clearSelection() {
    this.list.clearSelection();
    this.selectedShipments = [];
  }
}
